import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { Task, Test } from '@/types';
import PageLayout from '@/shared/ui/layout/PageLayout';
import { DetailSection } from '@/shared/ui/layout';
import './styles.css';

const TaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [testsUsingTask, setTestsUsingTask] = useState<Test[]>([]);
  const [isUsedInTests, setIsUsedInTests] = useState(false);
  const [isAddingAttachments, setIsAddingAttachments] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) return;
        
        // Fetch task details
        const taskData = await api.tasks.getById(id);
        setTask(taskData);
        
        // Check if task is used in any tests
        const allTests = await api.tests.getAll();
        const testsWithTask = allTests.filter((test: Test) => {
          // Handle both string IDs and Task objects in the tasks array
          const taskIds = Array.isArray(test.tasks) 
            ? test.tasks.map((t: any) => typeof t === 'string' ? t : t._id)
            : [];
          return taskIds.includes(id);
        });
        
        setTestsUsingTask(testsWithTask);
        setIsUsedInTests(testsWithTask.length > 0);
        
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to fetch task');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDelete = async () => {
    if (!task?._id || !window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await api.tasks.delete(task._id);
      toast.success('Task deleted successfully!');
      navigate('/tasks');
    } catch (err: unknown) {
      const apiError = err as ApiError;
      let errorMessage = apiError.message || 'Failed to delete task';
      if (apiError.data && apiError.data.tests) {
        const testNames = apiError.data.tests
          .map((test) => test.name)
          .join(', ');
        errorMessage = `Cannot delete task: It is used in the following tests: ${testNames}`;
      }
      toast.error(errorMessage);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setAttachmentFiles(filesArray);
    }
  };

  const handleAddAttachments = async () => {
    if (!task?._id || attachmentFiles.length === 0) return;

    try {
      setIsAddingAttachments(true);

      const formData = new FormData();
      attachmentFiles.forEach(file => {
        formData.append('attachments', file);
      });

      const updatedTask = await api.tasks.addAttachments(task._id, formData);
      setTask(updatedTask);
      setAttachmentFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast.success('Attachments added successfully!');
      
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add attachments');
    } finally {
      setIsAddingAttachments(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!task) return <div>Task not found</div>;

  const actions = (
    <div className="actions-group">
      <div className="tooltip-container" data-position="bottom">
        <button 
          onClick={() => !isUsedInTests && navigate(`/tasks/${task._id}/edit`)} 
          className={`button edit-primary ${isUsedInTests ? 'disabled' : ''}`}
        >
          Edit
        </button>
        {isUsedInTests && (
          <div className="tooltip">
            Cannot edit task: It is used in {testsUsingTask.length} {testsUsingTask.length === 1 ? 'test' : 'tests'}: {testsUsingTask.map(t => t.name).join(', ')}
          </div>
        )}
      </div>
      
      <div className="tooltip-container" data-position="bottom">
        <button 
          onClick={isUsedInTests ? undefined : handleDelete} 
          className={`button danger ${isUsedInTests ? 'disabled' : ''}`}
        >
          Delete
        </button>
        {isUsedInTests && (
          <div className="tooltip">
            Cannot delete task: It is used in {testsUsingTask.length} {testsUsingTask.length === 1 ? 'test' : 'tests'}: {testsUsingTask.map(t => t.name).join(', ')}
          </div>
        )}
      </div>
    </div>
  );

  const detailItems = [
    {
      label: 'Category:',
      value: task.category
    },
    {
      label: 'Scoring Method:',
      value: task.scoringMethod === 'numerical' 
        ? `Numerical (${task.scoringScale?.min} - ${task.scoringScale?.max})` 
        : 'Binary (Pass/Fail)'
    }
  ];

  return (
    <PageLayout
      title={task.title}
      actions={actions}
    >
      <div className="task-detail">
        <DetailSection items={detailItems} />

        {/* Task Content Card */}
        <div className="content-card">
          <div className="content-section">
            <h3>Content</h3>
            <div className="content-box">
              <div className="text-content">
                {task.content}
              </div>
            </div>
          </div>

          {task.scoringRules && (
            <div className="content-section">
              <h3>Scoring Rules</h3>
              <div className="content-box">
                <div className="text-content">
                  {task.scoringRules}
                </div>
              </div>
            </div>
          )}

          {task.solution && (
            <div className="content-section">
              <h3>Solution</h3>
              <div className="content-box">
                <div className="text-content">
                  {task.solution}
                </div>
              </div>
            </div>
          )}

          {task.attachments && task.attachments.length > 0 && (
            <div className="content-section">
              <h3>Attachments</h3>
              <div className="attachments-list">
                {task.attachments.map((attachment) => {
                  const isImage = attachment.mimetype.startsWith('image/');
                  const isPdf = attachment.mimetype === 'application/pdf';
                  const downloadUrl = api.tasks.getAttachmentUrl(task._id!, attachment._id!);
                  
                  // Function to handle drag start
                  const handleDragStart = (e: React.DragEvent) => {
                    // Set the drag data with the download URL and filename
                    e.dataTransfer.setData('text/uri-list', downloadUrl);
                    e.dataTransfer.setData('text/plain', downloadUrl);
                    e.dataTransfer.setData('DownloadURL', `${attachment.mimetype}:${attachment.originalname}:${downloadUrl}`);
                    // Set the drag effect to 'copy' to indicate a file copy operation
                    e.dataTransfer.effectAllowed = 'copy';
                  };
                  
                  return (
                    <div 
                      key={attachment._id} 
                      className="attachment-item"
                      draggable="true"
                      onDragStart={handleDragStart}
                      title="Drag to desktop or other applications"
                    >
                      {isImage ? (
                        <div className="image-preview">
                          <img src={downloadUrl} alt={attachment.originalname} />
                        </div>
                      ) : (
                        <div className="file-icon">
                          {isPdf ? '📄 PDF' : '📄'}
                        </div>
                      )}
                      <div className="attachment-info">
                        <span className="attachment-name">{attachment.originalname}</span>
                        <a 
                          href={downloadUrl} 
                          download={attachment.originalname}
                          className="download-link"
                        >
                          Download
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Add Attachments Card */}
        <div className="content-card attachment-card">
          <p className="info-text">
            You can add attachments to this task even if it's used in tests.
          </p>
          <div className="attachment-upload">
            <input 
              type="file" 
              multiple 
              onChange={handleFileChange} 
              ref={fileInputRef}
              className="file-input"
            />
            <button 
              onClick={handleAddAttachments} 
              disabled={attachmentFiles.length === 0 || isAddingAttachments}
              className="button primary"
            >
              {isAddingAttachments ? 'Adding...' : 'Add Attachments'}
            </button>
          </div>
          {attachmentFiles.length > 0 && (
            <div className="selected-files">
              <h4>Selected Files:</h4>
              <ul>
                {attachmentFiles.map((file, index) => (
                  <li key={index}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default TaskDetail; 